import React from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { Image } from '@swyftx/aviary/atoms/Image';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';

import { observer } from 'mobx-react-lite';

import { GridOverlay } from './GridOverlay';

type Props = {
  title: string;
  content: string;
  className?: string;
  imgSrc: string;
  ignoreEntity?: boolean;
  usePalette?: boolean;
  primaryActionTitle?: string;
  primaryActionOnClick?: () => void;
  secondaryActionTitle?: string;
  secondaryActionOnClick?: () => void;
  showSecondaryTrailingArrow?: boolean;
  secondaryTrailingIcon?: React.ReactElement<any, string | React.JSXElementConstructor<any>> | undefined;
};

const FeaturePromoBanner: React.FC<Props> = observer(
  ({
    className,
    title,
    content,
    imgSrc,
    ignoreEntity = false,
    primaryActionTitle,
    primaryActionOnClick,
    secondaryActionTitle,
    secondaryActionOnClick,
    secondaryTrailingIcon,
    usePalette = false,
  }) => {
    const isXs = useTailwindBreakpoint('xs');

    return (
      <Card className='relative overflow-hidden bg-color-background-secondary'>
        {/* SVG grid pattern overlay */}
        <GridOverlay className='pointer-events-none absolute inset-0 z-0 w-full scale-[3.5] object-cover opacity-30 md:scale-[1.5]' />

        <FlexLayout
          spacing={24}
          justifyContent='space-between'
          className={`relative z-10 flex-col rounded-16 bg-[linear-gradient(278deg,_transparent_15.35%,_var(--color-gradient-overlay)_55.29%)] p-24 lg:flex-row ${className}`}
        >
          <FlexLayout spacing={24} alignItems='center' direction={isXs ? 'column' : 'row'}>
            <Image
              image={imgSrc}
              usePalette={usePalette}
              ignoreEntity={ignoreEntity}
              className='h-[74px] min-w-[74px]'
            />
            <FlexLayout direction='column' justifyContent='center'>
              <Heading
                size={isXs ? 'h4' : 'h3'}
                color='inverse'
                weight={isXs ? 'normal' : 'light'}
                className={isXs ? 'text-center' : 'text-left'}
              >
                {title}
              </Heading>
              <Body size={isXs ? 'medium' : 'large'} color='inverse' className={isXs ? 'text-center' : 'text-left'}>
                {content}
              </Body>
            </FlexLayout>
          </FlexLayout>

          <FlexLayout spacing={16} alignItems='center' justifyContent={isXs ? 'center' : 'start'}>
            {primaryActionTitle && primaryActionOnClick && (
              <Button color='accent' size='lg' onClick={primaryActionOnClick}>
                {primaryActionTitle}
              </Button>
            )}
            {secondaryActionTitle && secondaryActionOnClick && (
              <Button
                variant='ghost'
                size='lg'
                onClick={secondaryActionOnClick}
                color='inverse'
                trailingIcon={secondaryTrailingIcon}
              >
                {secondaryActionTitle}
              </Button>
            )}
          </FlexLayout>
        </FlexLayout>
      </Card>
    );
  },
);

export { FeaturePromoBanner };
